import dayjs from "dayjs"
import {
    PAYER_BORROWER,
    effectiveHardPullPayer,
    hasTriMergePricing,
} from "../Management/CreditReportSetup/creditPricingUtils"

export const VENDORS_LIST = {
    KCBCredit: "KCB Credit",
    AdvantageCredit: "Advantage Credit",
    CreditTechnology: "Credit Technology",
    CreditTechnologies: "Credit Technologies",
    PremiumCreditBureau: "Premium Credit Bureau",
    PremiumCreditDataCDC: "Premium Credit Data (CDC)",
    Xactus: "Xactus",
    Mortgage: "Mortgage",
}

export const REPORT_TYPE_BY_RADIO = {
    SoftPull: "softCheck",
    HardPull: "hardCheck",
}

export function maskSSN(ssn) {
    if (!ssn) return "--"
    return `${ssn}`.replace(/(\d{3}-\d{2}-)(\d{4})/g, "*** - ** - $2")
}

export function formatParticipantName(participant) {
    if (!participant) return "--"
    const parts = [participant.firstName, participant.middleName, participant.lastName, participant.suffix].filter(Boolean)
    return parts.length ? parts.join(" ") : "--"
}

/** Label for credit-pull error messages (individual or joint subjects). */
export function formatCreditPullSubjectsLabel(subjectPartyIds, participantsById, pullMode = "individual") {
    const ids = (subjectPartyIds || []).filter(Boolean)
    const names = ids.map((id) => {
        const p = participantsById?.[id] || (Array.isArray(participantsById)
            ? participantsById.find((x) => x.partyId === id)
            : null)
        return formatParticipantName(p)
    }).filter((name) => name && name !== "--")
    if (!names.length) return ""
    const label = names.join(" + ")
    if (String(pullMode || "").toLowerCase() === "joint" && names.length > 1) {
        return `${label} (joint)`
    }
    return label
}

export function withCreditPullSubjectError(err, subjectLabel) {
    const base = err?.message || "An unexpected error occurred. Please try again later."
    const label = String(subjectLabel || "").trim()
    if (!label || base.includes(label)) return new Error(base)
    return new Error(`${label} — ${base}`)
}

export function formatParticipantAddress(participant) {
    const addr = participant?.presentAddress || {}
    const street = addr.street || addr.address || ""
    if (!street) return "--"
    return [street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ")
}

export function hasFilledCreditPullSsn(participant) {
    return normalizeSsnDigits(participant?.socialSecurity).length > 0
}

export function hasFilledPresentResidentialAddress(participant) {
    const addr = participant?.presentAddress || {}
    return `${addr.street || addr.address || ""}`.trim() !== ""
}

/** Missing SSN / current residential address for borrowers about to be pulled. */
export function listCreditPullProfileGaps(subjects) {
    return (subjects || []).map((participant) => {
        const missing = []
        if (!hasFilledCreditPullSsn(participant)) missing.push("SSN")
        if (!hasFilledPresentResidentialAddress(participant)) missing.push("Current residential address")
        if (!missing.length) return null
        return {
            partyId: participant.partyId,
            name: formatParticipantName(participant),
            missing,
        }
    }).filter(Boolean)
}

export function listParticipants(application) {
    const order = application?.participantOrder || []
    const byId = application?.participantsById || {}
    return order.map((partyId) => byId[partyId]).filter(Boolean)
}

export function buildAllCreditPullOperations(application, spousePullModeChoice = "joint") {
    const participants = listParticipants(application)
    if (!participants.length) return []

    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = (application?.cohabitingPartyId || "").trim()
    const pulled = new Set()
    const ops = []

    if (cohabitingPartyId && primaryPartyId) {
        if (spousePullModeChoice === "individual") {
            ops.push({ partyId: primaryPartyId, pullMode: "individual" })
            ops.push({ partyId: cohabitingPartyId, pullMode: "individual" })
            pulled.add(primaryPartyId)
            pulled.add(cohabitingPartyId)
        } else {
            ops.push({ partyId: primaryPartyId, pullMode: "joint" })
            pulled.add(primaryPartyId)
            pulled.add(cohabitingPartyId)
        }
    } else if (primaryPartyId) {
        ops.push({ partyId: primaryPartyId, pullMode: "individual" })
        pulled.add(primaryPartyId)
    }

    participants.forEach((participant) => {
        if (!pulled.has(participant.partyId)) {
            ops.push({ partyId: participant.partyId, pullMode: "individual" })
            pulled.add(participant.partyId)
        }
    })

    return ops
}

export function resolveSpousePairJointCardFlags(participant, index, participants, application) {
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = (application?.cohabitingPartyId || "").trim()
    if (!cohabitingPartyId) {
        return { isJointPair: false, isFirstJoint: false, isSecondJoint: false }
    }
    const firstIdx = participants.findIndex((p) => p.partyId === primaryPartyId)
    const secondIdx = participants.findIndex((p) => p.partyId === cohabitingPartyId)
    if (firstIdx < 0 || secondIdx < 0) {
        return { isJointPair: false, isFirstJoint: false, isSecondJoint: false }
    }
    const [lo, hi] = firstIdx < secondIdx ? [firstIdx, secondIdx] : [secondIdx, firstIdx]
    const isFirstJoint = index === lo
    const isSecondJoint = index === hi
    return {
        isJointPair: isFirstJoint || isSecondJoint,
        isFirstJoint,
        isSecondJoint,
    }
}

export function canChooseCreditPullMode(partyId, application) {
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = (application?.cohabitingPartyId || "").trim()
    return !!(cohabitingPartyId && (partyId === primaryPartyId || partyId === cohabitingPartyId))
}

export function resolvePullSubjects(partyId, application, pullModeChoice) {
    const primaryPartyId = application?.primaryPartyId || application?.participantOrder?.[0] || ""
    const cohabitingPartyId = (application?.cohabitingPartyId || "").trim()
    const isSpousePair = cohabitingPartyId && (partyId === primaryPartyId || partyId === cohabitingPartyId)
    if (!isSpousePair) {
        return { pullMode: "individual", subjectPartyIds: [partyId].filter(Boolean) }
    }
    if (pullModeChoice === "individual") {
        return { pullMode: "individual", subjectPartyIds: [partyId].filter(Boolean) }
    }
    return {
        pullMode: "joint",
        subjectPartyIds: [primaryPartyId, cohabitingPartyId].filter(Boolean),
    }
}

export function formatPersonNameFromReport(borrower) {
    if (!borrower || !borrower.lastName) return "--"
    let name = borrower.lastName
    if (borrower.firstName) name += `, ${borrower.firstName}`
    if (borrower.middleName) name += ` ${borrower.middleName}`
    if (borrower.suffixName) name += ` ${borrower.suffixName}`
    return name
}

export function normalizeSsnDigits(ssn) {
    return `${ssn || ""}`.replace(/-/g, "").trim()
}

export function convertParties(data) {
    return (data?.borrowers || []).map((borrower) => {
        const credit = (data.scoreModels || [])
            .filter((model) => model.role === borrower.role)
            .map((model) => ({
                value: model.scoreValue,
                title: model.modelName || model.modelNameType,
                bureau: model.bureau || "",
                notScored: Boolean(model.exclusionReason) || !(model.scoreValue > 0),
                factors: model.factors ? model.factors.map((f) => f.text || f) : [],
            }))
        return {
            name: formatPersonNameFromReport(borrower),
            role: borrower.role,
            credit,
            sourceReportId: data?.id || null,
        }
    })
}

function formatParticipantNameReportStyle(participant) {
    if (!participant?.lastName) return formatParticipantName(participant)
    let name = participant.lastName
    if (participant.firstName) name += `, ${participant.firstName}`
    if (participant.middleName) name += ` ${participant.middleName}`
    if (participant.suffix) name += ` ${participant.suffix}`
    return name
}

function findBorrowerInReport(report, ssn) {
    const want = normalizeSsnDigits(ssn)
    if (!want || !report) return null
    return (report.borrowers || []).find((borrower) => (
        normalizeSsnDigits(borrower.ssn || borrower.SSN) === want
    )) || null
}

function creditScoresForBorrower(report, borrower) {
    if (!report || !borrower) return []
    return (report.scoreModels || [])
        .filter((model) => model.role === borrower.role)
        .map((model) => ({
            value: model.scoreValue,
            title: model.modelName || model.modelNameType,
            bureau: model.bureau || "",
            notScored: Boolean(model.exclusionReason) || !(model.scoreValue > 0),
            factors: model.factors ? model.factors.map((f) => f.text || f) : [],
        }))
}

function findBorrowerByRole(report, role) {
    if (!report || !role) return null
    return (report.borrowers || []).find((borrower) => borrower.role === role) || null
}

function pickReportAndBorrower(participant, reports, selected, primaryPartyId, cohabitingPartyId) {
    const tryMatch = (report) => {
        if (!report) return null
        const bySsn = findBorrowerInReport(report, participant.socialSecurity)
        if (bySsn) return { report, borrower: bySsn }
        // Joint fallback: map Primary/Secondary to primary / spouse when SSN is missing or differs from vendor demo data.
        if ((report.borrowers || []).length === 2) {
            if (participant.partyId === primaryPartyId) {
                const borrower = findBorrowerByRole(report, "Primary")
                if (borrower) return { report, borrower }
            }
            if (cohabitingPartyId && participant.partyId === cohabitingPartyId) {
                const borrower = findBorrowerByRole(report, "Secondary")
                if (borrower) return { report, borrower }
            }
        }
        return null
    }

    // Dropdown selection is authoritative: only show scores from the selected report.
    // Do not fall back to other pulls — otherwise switching the select looks like a no-op.
    if (selected) {
        return tryMatch(selected) || { report: null, borrower: null }
    }

    for (const report of reports) {
        const match = tryMatch(report)
        if (match) return match
    }
    return { report: null, borrower: null }
}

/**
 * Credit reports tab: one block per subject on the selected report only
 * (aligned with legacy: people not on this pull are omitted entirely).
 * Heading uses the bureau/report name so it matches the report Select label.
 */
export function buildCreditReportsTabParties(application, reports = [], selectedReportId = "") {
    const participants = listParticipants(application)
    if (!participants.length) {
        const selected = reports.find((item) => `${item.id}` === `${selectedReportId}`) || reports[0]
        return selected ? convertParties(selected) : []
    }

    const selected = reports.find((item) => `${item.id}` === `${selectedReportId}`) || null
    const primaryPartyId = application?.primaryPartyId || participants[0]?.partyId || ""
    const cohabitingPartyId = `${application?.cohabitingPartyId || ""}`.trim()

    return participants.map((participant) => {
        const { report, borrower } = pickReportAndBorrower(
            participant,
            reports,
            selected,
            primaryPartyId,
            cohabitingPartyId,
        )
        if (!borrower) return null
        const reportName = formatPersonNameFromReport(borrower)
        const appName = formatParticipantNameReportStyle(participant)
        return {
            partyId: participant.partyId,
            name: reportName && reportName !== "--" ? reportName : appName,
            role: borrower.role || "",
            credit: creditScoresForBorrower(report, borrower),
            sourceReportId: report?.id || null,
            pulled: true,
            reportName,
        }
    }).filter(Boolean)
}

export function reportSummaryTitle(report) {
    const count = report?.borrowers?.length || 0
    return count > 1 ? "Joint Credit Summary" : "Individual credit summary"
}

export function loadConsentState(loanId, participants) {
    // Seed from the participant records, i.e. the consent the borrower actually gave and
    // that the server stored. localStorage only ever holds what this browser happened to
    // confirm, so on another machine (or after a cache clear) a borrower who consented
    // months ago used to show up as "pending" — while the line right below the badge,
    // which reads the same server field, said they had consented.
    const fallback = {}
    participants.forEach((p) => {
        fallback[p.partyId] = {
            isConsent: Boolean(p?.creditconsentdate),
            isConsentType: p?.creditconsentdate ? "borrower" : "",
        }
    })
    try {
        const raw = localStorage.getItem(`creditConsentV2:${loanId}`)
        if (!raw) return fallback
        const parsed = JSON.parse(raw)
        if (parsed.loanID !== loanId || !parsed.consents) return fallback
        const merged = { ...fallback }
        Object.entries(parsed.consents).forEach(([partyId, value]) => {
            if (!merged[partyId]) return
            // Local state may only ADD a consent the LO confirmed by phone/in person; it
            // must never revoke one the server already recorded.
            if (merged[partyId].isConsent) return
            merged[partyId] = {
                isConsent: !!value?.isConsent,
                isConsentType: value?.isConsentType || "",
            }
        })
        return merged
    } catch {
        return fallback
    }
}

export function saveConsentState(loanId, consents) {
    localStorage.setItem(`creditConsentV2:${loanId}`, JSON.stringify({
        loanID: loanId,
        consents,
    }))
}

const CREDIT_PULL_PREFS_PREFIX = "creditPullPrefsV2"

function decodeJwtPayload(token) {
    try {
        const part = (token || "").split(".")[1]
        if (!part) return null
        const b64 = part.replace(/-/g, "+").replace(/_/g, "/")
        const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4)
        return JSON.parse(atob(padded))
    } catch {
        return null
    }
}

function creditPullPrefsStorageKey() {
    try {
        const token = sessionStorage.getItem("ZeitroA")
        const payload = decodeJwtPayload(token)
        // LO tokens use loanofficerid / email (not id/sub).
        const uid = payload?.loanofficerid || payload?.email || payload?.id || payload?.customerid || ""
        return uid ? `${CREDIT_PULL_PREFS_PREFIX}:${uid}` : CREDIT_PULL_PREFS_PREFIX
    } catch {
        return CREDIT_PULL_PREFS_PREFIX
    }
}

function creditPullPrefsCandidateKeys() {
    const primary = creditPullPrefsStorageKey()
    const keys = [primary]
    if (primary !== CREDIT_PULL_PREFS_PREFIX) keys.push(CREDIT_PULL_PREFS_PREFIX)
    return keys
}

function normalizeAccountIdentity(value) {
    return String(value || "").trim().toLowerCase()
}

function accountVendor(acc) {
    return acc?.Vendor ?? acc?.vendor ?? ""
}

function accountUsername(acc) {
    return acc?.Username ?? acc?.UserName ?? acc?.username ?? ""
}

/** Last Soft/Hard + account choice for V2 credit-check modal (no password). */
export function loadCreditPullPrefs() {
    const readFrom = (storage) => {
        if (!storage) return null
        for (const key of creditPullPrefsCandidateKeys()) {
            const raw = storage.getItem(key)
            if (!raw) continue
            try {
                const parsed = JSON.parse(raw)
                if (parsed?.creditRadio !== "SoftPull" && parsed?.creditRadio !== "HardPull") continue
                return {
                    creditRadio: parsed.creditRadio,
                    vendor: String(parsed.vendor || ""),
                    username: String(parsed.username || ""),
                }
            } catch {
                // try next key
            }
        }
        return null
    }
    try {
        return readFrom(localStorage) || readFrom(sessionStorage)
    } catch {
        return null
    }
}

export function saveCreditPullPrefs({ creditRadio, vendor, username }) {
    if (creditRadio !== "SoftPull" && creditRadio !== "HardPull") return
    const payload = JSON.stringify({
        creditRadio,
        vendor: vendor || "",
        username: username || "",
        savedAt: Date.now(),
    })
    try {
        localStorage.setItem(creditPullPrefsStorageKey(), payload)
    } catch {
        // ignore quota / private mode
    }
    try {
        // Same-tab backup if localStorage is flaky in some browsers.
        sessionStorage.setItem(creditPullPrefsStorageKey(), payload)
    } catch {
        // ignore
    }
}

/**
 * Restore Soft/Hard + account from prefs against current accountOptions.
 * Falls back to IsDefault for that type when the saved account is gone.
 */
export function applyCreditPullPrefs(accountOptions, prefs) {
    if (!prefs?.creditRadio) {
        return { creditRadio: "", selectAccount: null }
    }
    const accounts = accountOptions?.[prefs.creditRadio] || []
    if (!accounts.length) {
        // Keep Soft/Hard even if account list is temporarily empty.
        return { creditRadio: prefs.creditRadio, selectAccount: null }
    }
    const wantVendor = normalizeAccountIdentity(prefs.vendor)
    const wantUser = normalizeAccountIdentity(prefs.username)
    let index = accounts.findIndex(
        (acc) => normalizeAccountIdentity(accountVendor(acc)) === wantVendor
            && normalizeAccountIdentity(accountUsername(acc)) === wantUser,
    )
    if (index < 0 && (wantVendor || wantUser)) {
        index = accounts.findIndex(
            (acc) => normalizeAccountIdentity(accountVendor(acc)) === wantVendor
                || normalizeAccountIdentity(accountUsername(acc)) === wantUser,
        )
    }
    if (index < 0) {
        index = accounts.findIndex((acc) => acc.IsDefault)
    }
    if (index < 0) {
        index = 0
    }
    return {
        creditRadio: prefs.creditRadio,
        selectAccount: { ...accounts[index], index },
    }
}

export async function fetchCreditReports(loanId, pageSize = 50) {
    const token = sessionStorage.getItem("ZeitroA")
    const response = await fetch(`/api/v1/credit/getReports?loanId=${encodeURIComponent(loanId)}&pageSize=${pageSize}`, {
        method: "get",
        headers: {
            Authorization: "Bearer " + token,
            Cache: "no-cache",
        },
    })
    const body = await response.json()
    if (body.code !== 0) {
        throw new Error(body.errMessage || "Failed to load credit reports")
    }
    return body.data || []
}

/**
 * Collect a report whose vendor order was still processing. This is a status query on the
 * existing order — it opens no new credit inquiry and is not billable.
 * Resolves to { report, pending }.
 */
export async function resumeCreditReport(reportId, account) {
    const token = sessionStorage.getItem("ZeitroA")
    const response = await fetch("/api/v1/credit/resumeReport", {
        method: "POST",
        headers: {
            Authorization: "Bearer " + token,
            Cache: "no-cache",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(account ? { id: reportId, account } : { id: reportId }),
    })
    const body = await response.json()
    if (!response.ok || body.code !== 0) {
        throw new Error(body.errMessage || "Failed to collect the credit report")
    }
    return body.data || { report: null, pending: true }
}

/**
 * Ask the vendor whether these credentials work, before they are saved. Resolves to
 * { ok: true } or { ok: false, message }. Never pulls credit for a borrower.
 */
export async function verifyCreditAccount(account) {
    const token = sessionStorage.getItem("ZeitroA")
    const response = await fetch("/api/v1/credit/verifyAccount", {
        method: "POST",
        headers: {
            Authorization: "Bearer " + token,
            Cache: "no-cache",
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            vendor: account?.Vendor || "",
            userName: account?.Username || "",
            password: account?.Password || "",
        }),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || body.code !== 0) {
        return { ok: false, message: body.errMessage || "The credit vendor rejected these credentials." }
    }
    return { ok: true }
}

export async function fetchCreditAccounts() {
    const token = sessionStorage.getItem("ZeitroA")
    const headers = { Authorization: "Bearer " + token, Cache: "no-cache" }
    const [configRes, featureRes] = await Promise.all([
        fetch("/los/getcreditaccountconfig", { method: "GET", headers }),
        fetch("/los/getfeaturecustomization", { method: "GET", headers }),
    ])
    const creditAccountConfig = await configRes.json()
    const featureCustomization = await featureRes.json()
    const featureCreditAccounts = featureCustomization.CreditAccounts || []
    const adminCreditAccounts = (creditAccountConfig.Accounts || [])
        .filter((item) => item.Type)
        .map((account) => {
            const hasDefault = featureCreditAccounts.some(
                (featureAccount) => featureAccount.Type === account.Type && featureAccount.IsDefault,
            )
            return { ...account, IsDefault: !hasDefault, isAdminConfig: true }
        })
    const mergeAccounts = [...featureCreditAccounts, ...adminCreditAccounts]
    const sortAccounts = (accounts) => accounts.sort((a, b) => {
        if (a.IsDefault && !b.IsDefault) return -1
        if (!a.IsDefault && b.IsDefault) return 1
        return 0
    })
    return {
        SoftPull: sortAccounts(mergeAccounts.filter((acc) => acc.Type === "SoftPull")),
        HardPull: sortAccounts(mergeAccounts.filter((acc) => acc.Type === "HardPull")),
        BorrowerPaidPolicy: creditAccountConfig.BorrowerPaidPolicy || null,
        CreditPullPaymentPreference: featureCustomization.CreditPullPaymentPreference || null,
    }
}

/** Ops priced for borrower-paid send-link (partyIds expanded; amount filled server-side). */
export function buildBorrowerPaidPaymentOps(application, pullScope, pullPartyId, pullModeChoice) {
    if (pullScope === "single" && pullPartyId) {
        const subjects = resolvePullSubjects(pullPartyId, application, pullModeChoice)
        return [{
            pullMode: subjects.pullMode,
            partyIds: subjects.subjectPartyIds.filter(Boolean),
        }]
    }
    return buildAllCreditPullOperations(application, pullModeChoice).map((op) => {
        const subjects = resolvePullSubjects(op.partyId, application, op.pullMode)
        return {
            pullMode: subjects.pullMode,
            partyIds: subjects.subjectPartyIds.filter(Boolean),
        }
    })
}

export function estimateBorrowerPaidAmount(account, ops = []) {
    if (!account) return 0
    return ops.reduce((sum, op) => {
        const price = op.pullMode === "joint"
            ? Number(account.JointTriMergePrice || 0)
            : Number(account.IndividualTriMergePrice || 0)
        return sum + (price > 0 ? price : 0)
    }, 0)
}

export function isBorrowerPaidHardPull(creditRadio, account, policy, preference) {
    if (creditRadio !== "HardPull" || !account?.isAdminConfig) return false
    if (!hasTriMergePricing(account)) return false
    return effectiveHardPullPayer(policy, preference) === PAYER_BORROWER
}

/** Borrower name(s) for the report Select label; joint joined with " + ". */
export function reportSelectBorrowerLabel(report) {
    const names = (report?.borrowers || [])
        .map((borrower) => formatPersonNameFromReport(borrower))
        .filter((name) => name && name !== "--")
    return names.length ? names.join(" + ") : ""
}

/**
 * The vendor's status for a report: "Completed", "Processing", "Error", or "" when the
 * vendor said nothing. The backend has always sent this in `getReports`; the UI used to
 * ignore it and render every report as if it were finished.
 */
export function reportStatusCode(report) {
    return report?.status?.[0]?.statusCode || ""
}

/** True when the vendor finished the order and the report actually holds data. */
export function isReportComplete(report) {
    const code = reportStatusCode(report)
    // Older rows predate the status column; treat "has borrowers" as finished.
    if (!code) return Boolean(report?.borrowers?.length)
    return code === "Completed"
}

/** True when the vendor is still working on this order and it can still be collected. */
export function isReportPending(report) {
    return reportStatusCode(report) === "Processing"
}

/**
 * True when the bureau finished the order AND actually returned something to look at.
 * A pull can be "Completed" and still carry no tradelines or scores — e.g. the bureau
 * answers "*** NO RECORD FOUND ***" with CreditFileResultStatusType NoFileReturnedError.
 * That is a real outcome, not a report, and must not be rendered as an empty table.
 */
export function isReportUsable(report) {
    if (!isReportComplete(report)) return false
    return Boolean(report?.liabilities?.length || report?.scoreModels?.length)
}

/** What the bureau said about a report that came back empty, if anything. */
export function reportVendorMessage(report) {
    const first = (report?.errors || []).find((e) => e?.errorMessageText)
    if (!first) return ""
    const source = first.errorMessageCategoryCode ? `${first.errorMessageCategoryCode}: ` : ""
    return `${source}${String(first.errorMessageText).trim()}`
}

/** Short human label for a report that is not a usable result, or "" when it is fine. */
export function reportStatusLabel(report) {
    if (isReportPending(report)) return "Processing — not ready"
    if (!isReportComplete(report)) {
        return reportStatusCode(report) === "Error" ? "Failed" : "No report data"
    }
    if (!isReportUsable(report)) return "No records found"
    return ""
}

/**
 * `credit_report.created_at` is a `timestamp without time zone` holding Pacific wall-clock,
 * which Go serialises with a "Z" as if it were UTC. Parsing that normally shifts it by the
 * viewer's offset (a 14:37 PT pull displayed as 07:37). Drop the zone marker so the stored
 * wall-clock is shown verbatim.
 */
export function formatReportTimestamp(value, template = "MM/DD/YYYY HH:mm:ss") {
    if (!value) return "--"
    const naive = String(value).replace(/(?:Z|[+-]\d{2}:?\d{2})$/, "")
    const parsed = dayjs(naive)
    return parsed.isValid() ? parsed.format(template) : "--"
}

export function buildReportSelectOptions(reports) {
    return reports.map((item) => {
        const pullType = item.reportType === "softCheck" ? "Soft credit pull" : "Hard credit pull"
        const when = formatReportTimestamp(item.createdAt)
        const names = reportSelectBorrowerLabel(item)
        const base = names ? `${names} · ${pullType} ${when}` : `${pullType} ${when}`
        const status = reportStatusLabel(item)
        return {
            label: status ? `${base} · ${status}` : base,
            value: item.id,
        }
    })
}
